import { useTheme } from "@mui/material";
import { MessageCircle, Sparkles, Target } from "lucide-react";
import { type FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ownHomeImg from "shared/assets/inspiration/ins-001-own-home.jpg";
import {
	GoalCategoryTypeEnum,
	InspirationTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { AffordabilityEditSheet } from "web_myplann/Inspiration/Components/AffordabilityEditSheet";
import { ExpensesSheet } from "web_myplann/Inspiration/Components/ExpensesSheet";
import { InspirationDetailLayout } from "web_myplann/Inspiration/Components/InspirationDetailLayout";
import { OwnHomeContent } from "web_myplann/Inspiration/Components/OwnHomeContent";
import { OwnHomeCreateGoalSheet } from "web_myplann/Inspiration/Components/OwnHomeCreateGoalSheet";
import { OwnHomeEstimateSheet } from "web_myplann/Inspiration/Components/OwnHomeEstimateSheet";
import { useOpenGoalFormFromInspiration } from "web_myplann/Inspiration/Hooks/useOpenGoalFormFromInspiration";
import { useOwnHomeContext } from "web_myplann/Inspiration/Hooks/useOwnHomeContext";
import { useOwnHomeJourney } from "web_myplann/Inspiration/Hooks/useOwnHomeJourney";
import { computeGoalPreset } from "web_myplann/Inspiration/Utils/ownHomeMath";
import { useContactAdvisor } from "web_myplann/Shared/Hooks/useContactAdvisor";

const PageResources = Resources.Inspiration.OwnHome;

export const OwnHomeInspirationPage: FC = () => {
	const { t } = useResource();
	const theme = useTheme();
	const navigate = useNavigate();
	const { openChat } = useContactAdvisor();
	const { openGoalForm } = useOpenGoalFormFromInspiration();
	const {
		inputs,
		activeSheet,
		openSheet,
		closeSheet,
		submitQuick,
		submitExpenses,
		submitAffordabilityEdit,
	} = useOwnHomeJourney();
	const { context, isLoading } = useOwnHomeContext(inputs);

	const isCold = !context || context.state === "cold";

	const primaryCta = isCold
		? {
				label: t(PageResources.cta),
				icon: <Sparkles size={theme.iconSize.md} />,
				disabled: !context,
				onClick: () => openSheet("quick"),
			}
		: {
				label: t(PageResources.Result.createGoal),
				icon: <Target size={theme.iconSize.md} />,
				onClick: () => openSheet("create-goal"),
			};

	const goToFinances = () => navigate(Routing.Finances.Intro.path());

	const goalPreset = useMemo(
		() => (context ? computeGoalPreset(context) : null),
		[context],
	);

	const navigateToGoalForm = () => {
		if (!goalPreset) return;

		openGoalForm(
			{
				category: GoalCategoryTypeEnum.House,
				totalValue: goalPreset.totalValue,
				ownFunds: goalPreset.ownFunds,
				loanAmount: goalPreset.loanAmount,
			},
			InspirationTypeEnum.OwnHome,
		);
	};

	return (
		<>
			<InspirationDetailLayout
				scrollResetKey={isCold}
				image={ownHomeImg}
				category={t(PageResources.category)}
				title={t(PageResources.title)}
				subtitle={t(PageResources.subtitle)}
				primaryCta={primaryCta}
				secondaryCta={
					context && !isCold
						? {
								label: t(PageResources.Result.contactAdvisor),
								icon: <MessageCircle size={theme.iconSize.md} />,
								variant: "tertiary",
								onClick: () => openChat(t(PageResources.Result.advisorMessage)),
							}
						: undefined
				}
			>
				<OwnHomeContent
					context={context}
					isLoading={isLoading}
					onEditAffordability={() => openSheet("affordability-edit")}
					onAddExpenses={() => openSheet("expenses")}
				/>
			</InspirationDetailLayout>

			<OwnHomeEstimateSheet
				open={activeSheet === "quick"}
				onClose={closeSheet}
				onSubmit={submitQuick}
				onSaveToBilance={goToFinances}
			/>

			<ExpensesSheet
				open={activeSheet === "expenses"}
				initial={inputs.expenses}
				onClose={closeSheet}
				onSubmit={submitExpenses}
				onSaveToBilance={goToFinances}
			/>

			<AffordabilityEditSheet
				open={activeSheet === "affordability-edit"}
				initialIncome={inputs.income}
				initialExpenses={inputs.expenses}
				showExpenses={context?.state === "full"}
				onClose={closeSheet}
				onSubmit={submitAffordabilityEdit}
			/>

			{goalPreset && context && (
				<OwnHomeCreateGoalSheet
					open={activeSheet === "create-goal"}
					preset={goalPreset}
					rates={context.rates}
					onClose={closeSheet}
					onContinue={navigateToGoalForm}
				/>
			)}
		</>
	);
};
